import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calculator, ChevronRight, Info, LogIn, RefreshCw } from "lucide-react";
import { useSavedCalculatorResults } from "@/hooks/use-saved-calculator-results";
import { useAuth } from "@/components/auth/AuthProvider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function formatEur(n: number) {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}

interface FairDealEstimateCardProps {
  nursingHomePriceRange?: string | null;
  centerName: string;
}

export function FairDealEstimateCard({ nursingHomePriceRange, centerName }: FairDealEstimateCardProps) {
  const { user } = useAuth();
  const { data, isLoading } = useSavedCalculatorResults();

  // ── Not logged in ─────────────────────────────────────────────────────────
  if (!user) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" />
            Your Fair Deal Estimate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Sign in and complete the Fair Deal calculator to see your personalised
            weekly contribution estimate for {centerName}.
          </p>
          <div className="flex flex-col gap-2">
            <Button asChild size="sm" className="w-full">
              <Link to="/auth/login">
                <LogIn className="h-4 w-4 mr-2" />
                Sign in to see estimate
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="w-full">
              <Link to="/fair-deal-calculator">
                <Calculator className="h-4 w-4 mr-2" />
                Try the calculator
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Card className="border-primary/20 bg-primary/5 animate-pulse">
        <CardContent className="py-6">
          <div className="h-4 bg-muted rounded w-2/3 mb-3" />
          <div className="h-8 bg-muted rounded w-1/2 mb-2" />
          <div className="h-3 bg-muted rounded w-full" />
        </CardContent>
      </Card>
    );
  }

  // ── No saved calculation ───────────────────────────────────────────────────
  if (!data) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" />
            Your Fair Deal Estimate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Use the Fair Deal calculator to see what your weekly contribution would
            be — we'll save it here for every nursing home you view.
          </p>
          <Button asChild size="sm" className="w-full">
            <Link to="/fair-deal-calculator">
              <Calculator className="h-4 w-4 mr-2" />
              Calculate your contribution
              <ChevronRight className="h-4 w-4 ml-auto" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Has results ────────────────────────────────────────────────────────────
  const { weeklyContribution, stateSupport, nursingHomeCost } = data;

  // If the home has a price_range, try to parse a mid-point for comparison
  const homeCostForDisplay = nursingHomeCost > 0 ? nursingHomeCost : null;

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" />
            Your Fair Deal Estimate
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px] text-xs">
                Based on your saved Fair Deal calculator inputs. Your actual
                contribution is set by the HSE financial assessment.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Badge variant="secondary" className="w-fit text-xs">
          Based on your saved inputs
        </Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Primary number */}
        <div className="rounded-lg bg-background border border-border p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Your est. weekly contribution</p>
          <p className="text-3xl font-bold text-primary">
            {formatEur(weeklyContribution)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">per week</p>
        </div>

        {/* State support row */}
        {stateSupport !== null && stateSupport > 0 && homeCostForDisplay && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-md bg-background border border-border p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Home cost used</p>
              <p className="font-semibold">{formatEur(homeCostForDisplay)}<span className="text-xs font-normal text-muted-foreground">/wk</span></p>
            </div>
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">State covers</p>
              <p className="font-semibold text-green-700">{formatEur(stateSupport)}<span className="text-xs font-normal">/wk</span></p>
            </div>
          </div>
        )}

        {/* Home price range context */}
        {nursingHomePriceRange && (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">{centerName}</span> listed price range:{" "}
            <span className="font-medium">{nursingHomePriceRange}</span>
          </p>
        )}

        <div className="pt-1 border-t border-border space-y-2">
          <Button asChild variant="outline" size="sm" className="w-full">
            <Link to="/fair-deal-calculator">
              <RefreshCw className="h-3.5 w-3.5 mr-2" />
              Update my inputs
            </Link>
          </Button>
          <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
            Estimate only · Actual contribution set by HSE assessment ·{" "}
            <Link to="/fair-deal" className="underline underline-offset-2">
              Learn about Fair Deal
            </Link>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
